import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import Container from 'components/domain/container/Container';
import CurrentSearchSkeleton from 'components/domain/currentSearch/CurrentSearch.skeleton';
import { WishlistCompareViewContent } from 'components/domain/wishlist/WishlistCompareViewContent';
import { WishlistEmpty } from 'components/domain/wishlist/WishlistEmpty';
import { WishlistItemSelectSkeleton } from 'components/domain/wishlist/WishlistItemSelect.skeleton';
import WishlistListCompareViewContentSkeleton from 'components/domain/wishlist/WishlistListCompareViewContentSkeleton.skeleton';
import WishlistListViewSkeleton from 'components/domain/wishlist/WishlistListViewSkeleton.skeleton';
import { WishlistSidebar } from 'components/domain/wishlist/WishlistSidebar';
import { WishlistSideContent } from 'components/domain/wishlist/WishlistSideContent';
import { searchFormWrapCss } from 'fdr/components/candidate/fdr-search-control/form-layout';
import { FdrSearchControlSkeleton } from 'fdr/components/candidate/fdr-search-control/skeleton';
import { WishlistSelectedItemsProvider } from 'fdr/components/candidate/fdr-wishlist/fdr-wishlist-selected-items-context';
import NoSsr from 'fdr/components/local/no-ssr/no-ssr';
import { useFdrGetActiveWishlistQuery } from 'fdr/schemas/query/wishlist/fdr-get-active-wishlist.generated';
import { useWishlistViewType } from './fdr-use-wishlist-view-type';
import FdrWishlistListViewContent from './fdr-wishlist-list-view-content';
import { WishlistViewType } from './fdr-wishlist.constants';
import FdrWishlistFooterMapAccess from './map/fdr-wishlist-footer-map-access';

const WishlistContentWrapper = styled.div.attrs({
  'data-id': `wishlist-content`,
})(
  sx2CssThemeFn({
    backgroundColor: 'bgLightBlue',
    px: [4, 0],
  })
);

const WishlistContentContainer = styled(Container).attrs({
  'data-id': `container`,
})(
  {
    '.search-control-skeleton': {
      ...searchFormWrapCss,
    },
  },
  sx2CssThemeFn({
    display: 'flex',
    py: [4, 7],
    mx: [0, 'auto'],
    flexWrap: 'wrap',
    flexDirection: ['column', 'row'],
  })
);

const getPlaceholders = mobile => ({
  [WishlistViewType.List]: <WishlistListViewSkeleton />,
  [WishlistViewType.Compare]: (
    <>
      {mobile ? (
        <CurrentSearchSkeleton />
      ) : (
        <FdrSearchControlSkeleton
          maxTabs={2}
          maxInputs={2}
          height={56}
          className="search-control-skeleton"
        />
      )}
      <WishlistItemSelectSkeleton />
      <WishlistListCompareViewContentSkeleton />
    </>
  ),
});

const renderNotEmptyContent = (viewType: WishlistViewType) => {
  const contentMap = {
    [WishlistViewType.List]: <FdrWishlistListViewContent />,
    [WishlistViewType.Compare]: <WishlistCompareViewContent />,
  };

  return (
    <WishlistSelectedItemsProvider>
      <WishlistSidebar type={viewType} />
      <WishlistSideContent>{contentMap[viewType]}</WishlistSideContent>
      <FdrWishlistFooterMapAccess type={viewType} />
    </WishlistSelectedItemsProvider>
  );
};

export const FdrWishlistContent: React.FC = () => {
  const { mobile } = useDeviceType();
  const viewType = useWishlistViewType();

  const { data, loading: wishlistLoading } = useFdrGetActiveWishlistQuery({
    ssr: false,
  });

  const wishlistItemsCount =
    data?.fdrActiveWishlist?.wishlist?.wishlistItems?.length || 0;

  const placeholders = useMemo(() => getPlaceholders(mobile), [mobile]);

  return (
    <WishlistContentWrapper>
      <WishlistContentContainer>
        {wishlistLoading ? (
          <>
            <NoSsr>
              <WishlistSidebar type={viewType} />
              <WishlistSideContent>
                {placeholders[viewType]}
              </WishlistSideContent>
            </NoSsr>
          </>
        ) : wishlistItemsCount > 0 ? (
          renderNotEmptyContent(viewType)
        ) : (
          <WishlistEmpty />
        )}
      </WishlistContentContainer>
    </WishlistContentWrapper>
  );
};
